import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./AiModelSelection.module.css";

export function AiModelSelection() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const AI_MODELS = [
    { id: "claude", name: "Claude", provider: "Anthropic", icon: "\u2726", color: "#cc785c" },
    { id: "gpt", name: "GPT", provider: "OpenAI", icon: "\u25C9", color: "#10a37f" },
    { id: "gemini", name: "Gemini", provider: "Google", icon: "\u25C6", color: "#4285f4" },
    { id: "qwen", name: "Qwen", provider: t.ai.modelLocalLlm, icon: "\u2B21", color: "#6366f1" },
    {
      id: "custom",
      name: t.ai.modelYourModel,
      provider: t.ai.modelAnyCompatible,
      icon: "\u2699",
      color: "#8b5cf6",
    },
  ];

  const PLANS = [
    {
      id: "byok",
      price: t.ai.planFreePrice,
      priceNote: t.ai.planFreeNote,
      features: t.ai.planFreeFeatures,
      cta: t.ai.planFreeCta,
      highlighted: false,
    },
    {
      id: "subscribe",
      price: t.ai.planSubPrice,
      priceNote: t.ai.planSubNote,
      features: t.ai.planSubFeatures,
      cta: t.ai.planSubCta,
      highlighted: true,
    },
  ];

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const header = el.querySelector(`.${styles.header}`);
      const modelCards = el.querySelectorAll(`.${styles.modelCard}`);
      const modelIcons = el.querySelectorAll(`.${styles.modelIcon}`);
      const planComparison = el.querySelector(`.${styles.planComparison}`);
      const planGlow = el.querySelector(`.${styles.planGlow}`);
      const tagline = el.querySelector(`.${styles.tagline}`);

      if (isMobile) {
        // Mobile: simple stagger fade-in, no pin
        const allElements = [header, ...modelCards, planComparison, tagline].filter(Boolean);

        gsap.set(allElements, { y: 30, opacity: 0 });
        gsap.to(allElements, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      } else {
        // Desktop: pinned scroll
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            pin: true,
            scrub: 1,
            start: "top top",
            end: "+=250%",
          },
        });

        // 1. Title + subtitle fade in
        gsap.set(header, { y: 40, opacity: 0 });
        tl.to(header, { y: 0, opacity: 1, duration: 1, ease: "power3.out" });

        // 2. Model cards slide in from right, staggered
        gsap.set(modelCards, { x: 80, opacity: 0 });
        tl.to(modelCards, {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
        });

        // 3. Icon pulse with brand color
        for (let i = 0; i < modelIcons.length; i++) {
          const icon = modelIcons[i];
          const model = AI_MODELS[i];
          gsap.set(icon, { scale: 1 });
          tl.to(
            icon,
            {
              scale: 1.3,
              duration: 0.3,
              ease: "power2.out",
              color: model.color,
              textShadow: `0 0 20px ${model.color}`,
            },
            "<+=0.1",
          );
          tl.to(icon, {
            scale: 1,
            duration: 0.3,
            ease: "power2.inOut",
          });
        }

        // 4. Plan comparison slides up from bottom
        gsap.set(planComparison, { y: 60, opacity: 0 });
        tl.to(planComparison, {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
        });

        // 5. Highlighted plan glow animation
        if (planGlow) {
          tl.to(planGlow, {
            opacity: 0.6,
            duration: 0.8,
            ease: "sine.inOut",
          });
          tl.to(planGlow, {
            opacity: 0.3,
            duration: 0.8,
            ease: "sine.inOut",
            yoyo: true,
            repeat: 1,
          });
        }

        // 6. Bottom tagline fades in
        gsap.set(tagline, { y: 20, opacity: 0 });
        tl.to(tagline, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" });
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.glowBg} />
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t.ai.modelTitle}</h2>
          <p className={styles.subtitle}>{t.ai.modelSubtitle}</p>
        </div>

        <div className={styles.modelCardsWrapper}>
          <div className={styles.modelCards}>
            {AI_MODELS.map((model) => (
              <div key={model.id} className={styles.modelCard}>
                <span
                  className={styles.modelIcon}
                  style={{ color: model.color }}
                  aria-hidden="true"
                >
                  {model.icon}
                </span>
                <span className={styles.modelName}>{model.name}</span>
                <span className={styles.modelProvider}>{model.provider}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.planComparison}>
          {PLANS.map((plan) => {
            const planName = plan.id === "byok" ? t.ai.planByok : t.ai.planSubscribe;
            return (
              <div
                key={plan.id}
                className={`${styles.planCard} ${plan.highlighted ? styles.planCardHighlighted : ""}`}
              >
                {plan.highlighted && <div className={styles.planGlow} />}
                <span className={styles.planBadge}>{planName}</span>
                <div>
                  <span className={styles.planPrice}>{plan.price}</span>
                  <span className={styles.planPriceNote}>{plan.priceNote}</span>
                </div>
                <ul className={styles.planFeatures}>
                  {plan.features.map((feature) => (
                    <li key={feature} className={styles.planFeature}>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`${styles.planCta} ${plan.highlighted ? styles.planCtaHighlighted : styles.planCtaDefault}`}
                >
                  {plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        <p className={styles.tagline}>{t.ai.modelTagline}</p>
      </div>
    </section>
  );
}
